import { differenceInCalendarDays, format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { calendarSpanInclusive, formatDateCell, parseTaskDay } from '../utils/epcScheduleDatesDisplay';
import {
  earnedDurationDays,
  floatToFirstFsSuccessor,
  naiveForecastFinish,
  progressVariancePct,
} from '../utils/taskForecast';
import { buildMiniTimeline } from './taskDetailModels';

export function TaskMiniTimeline({
  tasks,
  index,
  today,
}: {
  tasks: EpcScheduleTaskRow[];
  index: number;
  today: Date;
}) {
  const task = tasks[index]!;
  const model = buildMiniTimeline(tasks, index, today);
  const span = calendarSpanInclusive(task.start, task.finish);
  const earned = earnedDurationDays(task);
  const variance = progressVariancePct(task, today);
  const forecast = naiveForecastFinish(task, today);
  const planFinish = parseTaskDay(task.finish);
  const forecastDeltaDays =
    forecast && planFinish ? differenceInCalendarDays(forecast, planFinish) : null;
  const fsFloat = floatToFirstFsSuccessor(tasks, index);
  const floatTaskId = fsFloat != null ? tasks[fsFloat.successorIndex]?.id : null;

  return (
    <div className="rounded-xl border border-[#e8edf1] bg-white px-3.5 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <p className="mb-1 text-[9.5px] font-bold uppercase tracking-[0.1em] text-slate-400">
        Position in programme
      </p>
      {model ? (
        <div className="relative mt-1 h-[46px]">
          <div className="absolute inset-x-0 top-0 flex justify-between border-b border-[#f1f5f9] pb-0.5 text-[8.5px] font-bold uppercase tracking-[0.06em] text-slate-400">
            {model.monthTicks.map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>
          <div className="absolute inset-x-0 top-[22px] h-2.5 rounded-full bg-[#eef2f6]" />
          {model.prevBar ? (
            <div
              className="absolute top-[22px] h-2.5 rounded-full bg-slate-200"
              style={{ left: `${model.prevBar.leftPct}%`, width: `${model.prevBar.widthPct}%` }}
              title="Previous sibling task"
            />
          ) : null}
          {model.nextBar ? (
            <div
              className="absolute top-[22px] h-2.5 rounded-full bg-slate-200"
              style={{ left: `${model.nextBar.leftPct}%`, width: `${model.nextBar.widthPct}%` }}
              title="Next sibling task"
            />
          ) : null}
          <div
            className="absolute top-[19px] h-4 overflow-hidden rounded-md border border-[#aecfbe] bg-[#cfe3d8]"
            style={{ left: `${model.bar.leftPct}%`, width: `${model.bar.widthPct}%` }}
            title={`${formatDateCell(task.start)} → ${formatDateCell(task.finish)}`}
          >
            <span
              className="block h-full rounded-l-[5px] bg-[#006346]"
              style={{ width: `${Math.max(0, Math.min(100, model.bar.progressPct))}%` }}
            />
          </div>
          {model.todayPct != null ? (
            <div
              className="absolute bottom-0 top-[12px] w-0.5 bg-[#b45309]"
              style={{ left: `${model.todayPct}%` }}
            >
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[7px] font-extrabold text-[#b45309]">
                TODAY
              </span>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="py-3 text-[11px] text-slate-400">No start/finish dates on this line.</p>
      )}
      <div className="mt-2 flex flex-wrap justify-between gap-2 text-[9.5px] text-slate-500">
        <span>
          Earned{' '}
          <b className="text-slate-900">{earned != null && span != null ? `${earned}d of ${span}d` : '—'}</b>
        </span>
        <span>
          Variance{' '}
          <b className={variance != null && variance < 0 ? 'font-bold text-[#be123c]' : 'text-slate-900'}>
            {variance != null ? `${variance > 0 ? '+' : ''}${variance}% vs plan` : '—'}
          </b>
          {' · '}forecast finish{' '}
          <b className={forecastDeltaDays != null && forecastDeltaDays > 0 ? 'font-bold text-[#be123c]' : 'text-slate-900'}>
            {forecast
              ? `${format(forecast, 'dd MMM')}${forecastDeltaDays != null ? ` (${forecastDeltaDays >= 0 ? '+' : ''}${forecastDeltaDays}d)` : ''}`
              : '—'}
          </b>
        </span>
        <span>
          Float{fsFloat && floatTaskId != null ? ` to #${floatTaskId}` : ''}{' '}
          <b className={twMerge('text-slate-900', fsFloat?.critical && 'font-bold text-[#be123c]')}>
            {fsFloat ? (fsFloat.critical ? '0 days — critical' : `${fsFloat.days} days`) : '—'}
          </b>
        </span>
      </div>
    </div>
  );
}
